import { create } from 'zustand';

import { DEFAULT_TABLE_PAGE, DEFAULT_TABLE_PAGE_SIZE } from './constants';
import type {
  VisaoGeralElasticidadeFiltroOutrosBody,
  VisaoGeralElasticidadeTableBody,
  VisaoGeralElasticidadeTableResponse,
  VisaoGeralElasticidadeTableRowData,
  VisaoGeralElasticidadeTableSortDirection,
} from './types';

const createInitialTableBody = (): VisaoGeralElasticidadeTableBody => ({
  query: '',
  segmentation: '',
  subSegmentation: '',
  region: '',
  productBarcodes: [],
  manufacturers: [],
  brands: [],
  categories: [],
  sections: [],
  contents: [],
  elasticityLevels: [],
  cpiOperator: '',
  cpiValueFrom: 0,
  cpiValueTo: 0,
  optimalCpiOperator: '',
  optimalCpiValueFrom: 0,
  optimalCpiValueTo: 0,
});

const createInitialFiltersBody = (): VisaoGeralElasticidadeFiltroOutrosBody => ({
  ...createInitialTableBody(),
  barcode: '',
  barcodeCompetitor: '',
});

export const createInitialQuickFilterFields = (): Pick<
  VisaoGeralElasticidadeTableBody,
  'elasticityLevels' | 'cpiOperator' | 'cpiValueFrom' | 'cpiValueTo' | 'optimalCpiOperator' | 'optimalCpiValueFrom' | 'optimalCpiValueTo'
> => {
  const { elasticityLevels, cpiOperator, cpiValueFrom, cpiValueTo, optimalCpiOperator, optimalCpiValueFrom, optimalCpiValueTo } = createInitialTableBody();
  return { elasticityLevels, cpiOperator, cpiValueFrom, cpiValueTo, optimalCpiOperator, optimalCpiValueFrom, optimalCpiValueTo };
};

const syncFiltersBodyFromTableBody = (
  filtersBody: VisaoGeralElasticidadeFiltroOutrosBody,
  tableBody: VisaoGeralElasticidadeTableBody,
): VisaoGeralElasticidadeFiltroOutrosBody => ({
  ...filtersBody,
  ...tableBody,
  barcode: tableBody.productBarcodes[0] ?? '',
});

type State = {
  tableBody: VisaoGeralElasticidadeTableBody;
  appliedTableBody: VisaoGeralElasticidadeTableBody;
  filtersBody: VisaoGeralElasticidadeFiltroOutrosBody;
  tableData: VisaoGeralElasticidadeTableResponse;
  isEmptyResponse: boolean;
  marketShareOption: 'valor-vendas' | 'volume-vendas';
  competitorOption: 'produto-concorrente' | 'media-precos-concorrentes';
};

type Actions = {
  updateSharedFilters: (partial: Partial<VisaoGeralElasticidadeTableBody>) => void;
  setTableBody: (tableBody: VisaoGeralElasticidadeTableBody) => void;
  setAppliedTableBody: (tableBody: VisaoGeralElasticidadeTableBody) => void;
  updateFiltersBody: (partial: Partial<VisaoGeralElasticidadeFiltroOutrosBody>) => void;
  setTableData: (tableData: VisaoGeralElasticidadeTableResponse) => void;
  setTablePagination: (pagination: Partial<Pick<State, 'page' | 'size' | 'sortBy' | 'sortType'>>) => void;
  setMarketShareOption: (marketShareOption: 'valor-vendas' | 'volume-vendas') => void;
  setCompetitorOption: (competitorOption: 'produto-concorrente' | 'media-precos-concorrentes') => void;
  clearFilters: () => void;
  reset: () => void;
};

const applyTableBodyUpdate = (state: State, tableBody: VisaoGeralElasticidadeTableBody): Pick<State, 'tableBody' | 'filtersBody'> => ({
  tableBody,
  filtersBody: syncFiltersBodyFromTableBody(state.filtersBody, tableBody),
});

export const visaoGeralElasticidadeInitialState: State = {
  tableBody: createInitialTableBody(),
  appliedTableBody: createInitialTableBody(),
  filtersBody: createInitialFiltersBody(),
  tableData: {
    content: [],
    page: {
      number: DEFAULT_TABLE_PAGE,
      size: DEFAULT_TABLE_PAGE_SIZE,
      totalElements: 0,
      totalPages: 0,
    },
  },
  isEmptyResponse: false,
  marketShareOption: 'valor-vendas',
  competitorOption: 'produto-concorrente',
};

export const useVisaoGeralElasticidadeStore = create<State & Actions>(set => ({
  ...visaoGeralElasticidadeInitialState,

  updateSharedFilters: partial => set(state => applyTableBodyUpdate(state, { ...state.tableBody, ...partial })),

  setTableBody: tableBody => set(state => applyTableBodyUpdate(state, tableBody)),

  setAppliedTableBody: tableBody => set(() => ({ appliedTableBody: tableBody })),

  updateFiltersBody: partial =>
    set(state => ({
      filtersBody: { ...state.filtersBody, ...partial },
    })),

  setTableData: tableData =>
    set(() => ({
      tableData,
      isEmptyResponse: tableData.content.length === 0,
    })),

  setTablePagination: pagination => set(state => ({ ...state, ...pagination })),

  setMarketShareOption: marketShareOption => set(state => ({ ...state, marketShareOption })),

  setCompetitorOption: competitorOption => set(state => ({ ...state, competitorOption })),

  clearFilters: () =>
    set(state => ({
      tableBody: createInitialTableBody(),
      filtersBody: createInitialFiltersBody(),
      tableData: state.tableData,
      isEmptyResponse: state.isEmptyResponse,
    })),

  reset: () => set(visaoGeralElasticidadeInitialState),
}));
